import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  RecordingUploadSession,
  UploadSession,
} from '../../core/models/upload-session.model';
import { CloudUploadService } from '../../core/services/cloud-upload.service';
import { catchError, EMPTY, map } from 'rxjs';
import { LiveClassService } from '../../core/state/live-class/live-class.service';
import { ToastService } from '@black/ui';
import { ResourceService } from '../../core/state/resource/resource.service';
import { ResourceUploadStatus } from '../../core/state/resource/models/resource.model';
import {
  getEffectiveResourceAccess,
  ResourceAccessResult,
} from '@black/shared';

/** Max polling delay cap in ms. */
const MAX_POLL_DELAY_MS = 60_000;
/** Max number of poll attempts before giving up. */
const MAX_POLL_ATTEMPTS = 30;

@Component({
  selector: 'black-recording-card',
  standalone: false,
  templateUrl: './recording-card.component.html',
  styleUrl: './recording-card.component.css',
})
export class RecordingCardComponent implements OnInit, OnDestroy {
  private uploadService = inject(CloudUploadService);
  private liveClassService = inject(LiveClassService);
  private toastService = inject(ToastService);
  private resourceService = inject(ResourceService);

  @Input()
  session: RecordingUploadSession | null = null;

  @Input()
  subscriptions: any[] = [];

  @Input()
  liveClassOwnerId?: string;

  @Input()
  isAuthorized = true;

  uploadSession$: UploadSession | null = null;

  @Input()
  showDeleteButton = false;

  @Output()
  playVideoEvent = new EventEmitter<string>();

  @Output()
  sessionRemoved = new EventEmitter<string>();

  get resourceAccess(): ResourceAccessResult {
    if (!this.subscriptions || this.subscriptions.length === 0) {
      return {
        hasAccess: this.isAuthorized,
        accessType: this.isAuthorized ? 'FULL_COURSE' : 'NONE',
        effectiveSubscription: null,
        isPending: false,
        isRestrictedBySinglePass: false,
      };
    }
    return getEffectiveResourceAccess({
      subscriptions: this.subscriptions,
      resourceItem: {
        meetingId: this.session?.meetingId,
        sessionDate: this.session?.sessionDate,
      },
      liveClassOwnerId: this.liveClassOwnerId,
    });
  }

  get isAccessible(): boolean {
    return this.resourceAccess.hasAccess;
  }

  /** Upload status resolved by polling (null = not yet polled). */
  polledUploadStatus: ResourceUploadStatus | null = null;

  /** Whether a background poll is currently scheduled/running. */
  isPolling = false;

  private pollingTimeout: ReturnType<typeof setTimeout> | null = null;
  private pollAttempt = 0;

  ngOnInit(): void {
    if (this.session && this.session.uploadSessionId !== undefined) {
      this.uploadService.uploads$
        .pipe(
          map((sessions) =>
            sessions.find(
              (session) => session.id === this.session?.uploadSessionId
            )
          )
        )
        .subscribe({
          next: (session: UploadSession | undefined) => {
            if (session) {
              this.uploadSession$ = session;
            } else {
              this.uploadSession$ = null;
            }
          },
          error: () => {
            this.toastService.showToast(
              'Error fetching upload session!',
              'error'
            );
          },
        });
    }

    // Start background polling for URL / Google Drive uploads that are pending or uploading.
    if (
      this.session?.uploadSessionId === undefined &&
      this.session?.resource?._id &&
      (this.session.resource.uploadStatus ===
        ResourceUploadStatus.PENDING_UPLOAD ||
        this.session.resource.uploadStatus === ResourceUploadStatus.UPLOADING)
    ) {
      this.startPolling(this.session.resource._id);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private startPolling(resourceId: string): void {
    this.isPolling = true;
    this.scheduleNextPoll(resourceId);
  }

  private scheduleNextPoll(resourceId: string): void {
    if (this.pollAttempt >= MAX_POLL_ATTEMPTS) {
      this.isPolling = false;
      return;
    }

    const delay = Math.min(
      10_000 * Math.pow(2, Math.floor(this.pollAttempt / 10)),
      MAX_POLL_DELAY_MS
    );
    this.pollAttempt++;

    this.pollingTimeout = setTimeout(() => {
      this.resourceService.getById(resourceId).subscribe({
        next: (resource) => {
          this.polledUploadStatus = resource.uploadStatus ?? null;
          if (
            resource.uploadStatus === ResourceUploadStatus.COMPLETED ||
            resource.uploadStatus === ResourceUploadStatus.FAILED
          ) {
            this.isPolling = false;
          } else {
            this.scheduleNextPoll(resourceId);
          }
        },
        error: () => {
          // On HTTP error keep retrying with the next back-off interval.
          this.scheduleNextPoll(resourceId);
        },
      });
    }, delay);
  }

  private stopPolling(): void {
    if (this.pollingTimeout !== null) {
      clearTimeout(this.pollingTimeout);
      this.pollingTimeout = null;
    }
    this.isPolling = false;
  }

  playVideo(sessionId?: string) {
    if (sessionId) this.playVideoEvent.emit(sessionId);
  }

  get sessionTitle() {
    return this.session?.sessionName;
  }

  get sessionDate() {
    return this.session?.sessionDate;
  }

  get sessionId() {
    return this.session?.sessionId;
  }

  deleteRecording(id?: string) {
    if (id)
      this.liveClassService
        .deleteRecording(id)
        .pipe(
          catchError((err) => {
            this.toastService.showToast('Failed to delete Recording!', 'error');
            return EMPTY;
          })
        )
        .subscribe(() => {
          this.sessionRemoved.emit(id);
          this.toastService.showToast(
            'Recoding deleted Successfully!',
            'success'
          );
        });
  }

  get isUploadComplete() {
    return (
      this.polledUploadStatus === ResourceUploadStatus.COMPLETED ||
      this.session?.resource?.uploadStatus === ResourceUploadStatus.COMPLETED ||
      this.uploadSession$?.uploadStatus === 'completed'
    );
  }

  get isUploadFailed() {
    return (
      this.polledUploadStatus === ResourceUploadStatus.FAILED ||
      this.session?.resource?.uploadStatus === ResourceUploadStatus.FAILED
    );
  }

  get borderClass() {
    if (this.isUploadComplete) return 'border-base-300';
    if (this.isUploadFailed) return 'border-error';
    if (this.isPolling) return 'border-warning';
    if (
      this.session?.resource?.uploadStatus !== ResourceUploadStatus.COMPLETED &&
      this.session?.uploadSessionId === undefined
    ) {
      return 'border-error';
    }
    return 'border-base-300';
  }
}
